import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { 
  FiSearch, 
  FiSettings, 
  FiPlus, 
  FiChevronDown,
  FiMail,
  FiMessageSquare,
  FiDownload,
  FiX,
  FiCheck,
  FiClock,
  FiPause,
  FiSquare,
  FiAlertTriangle,
  FiFileText,
  FiEye,
  FiFilter,
  FiUser,
  FiCalendar,
  FiTag,
  FiActivity,
  FiPlay,
  FiCheckCircle,
  FiFilePlus,
  FiClipboard,
  FiBarChart
} from 'react-icons/fi';

// Import the medical records service
import { medicalRecordsService } from '../../../PatientTreatmentDetails/services/medicalRecordService';
import { API_DOCTOR_LIST } from '../../../../helpers/urlHelper';

// Constants from the model
const RECORD_STATUS = {
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  PENDING: 'pending',
  ON_HOLD: 'on_hold'
};

const RECORD_PRIORITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

const RECORD_CATEGORY = {
  DIAGNOSIS: 'diagnosis',
  TREATMENT: 'treatment',
  PROCEDURE: 'procedure',
  MEDICATION: 'medication',
  LAB_RESULT: 'lab_result',
  IMAGING: 'imaging',
  CONSULTATION: 'consultation',
  FOLLOW_UP: 'follow_up',
  OTHER: 'other'
};

const RECORD_SEVERITY = {
  MILD: 'mild',
  MODERATE: 'moderate',
  SEVERE: 'severe'
};

const CONSTANTS = {
  PRIORITIES: Object.values(RECORD_PRIORITY),
  STATUSES: Object.values(RECORD_STATUS),
  CATEGORIES: Object.values(RECORD_CATEGORY),
  SEVERITIES: Object.values(RECORD_SEVERITY),
  MAX_TITLE_LENGTH: 200,
  MAX_DESCRIPTION_LENGTH: 1000,
  MAX_NOTES_LENGTH: 2000
};

// Default form state
const DEFAULT_RECORD_STATE = {
  title: '',
  description: '',
  category: RECORD_CATEGORY.DIAGNOSIS,
  priority: RECORD_PRIORITY.MEDIUM,
  severity: RECORD_SEVERITY.MODERATE,
  status: RECORD_STATUS.IN_PROGRESS,
  fromDate: '',
  toDate: '',
  notes: '',
  icdCode: '',
  tags: [],
  patientId: '',
  doctorId: '',
  doctorName: ''
};

const DEFAULT_FILTERS_STATE = {
  status: 'All',
  priority: 'All',
  category: 'All',
  severity: 'All',
  doctorId: '',
  startDate: '',
  endDate: '',
  search: ''
};

const MedicalRecordTreatments = ({ patientId, onCountUpdate, componentName = 'medicalRecords' }) => {
  // State management
  const [medicalRecords, setMedicalRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [showMostRecentDropdown, setShowMostRecentDropdown] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState({
    records: false,
    submitting: false,
    doctors: false,
    recordDetails: false,
    updating: false,
    deleting: false
  });
  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');
  
  // Offcanvas states
  const [offcanvas, setOffcanvas] = useState({
    add: false,
    view: false,
    filter: false
  });
  
  // Dropdown states
  const [dropdowns, setDropdowns] = useState({
    mostRecent: false
  });
  
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS_STATE);
  const [newRecord, setNewRecord] = useState({
    ...DEFAULT_RECORD_STATE,
    patientId: patientId || ''
  });

  // Debug logging
  const logDebug = useCallback((message, data) => {
    console.log(`[MedicalRecords] ${message}:`, data);
  }, []);

  // Utility functions
  const formatDate = useCallback((dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '-';
    }
  }, []);

  const formatDateTime = useCallback((dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  }, []);

  const getStatusConfig = useCallback((status) => {
    const configs = {
      [RECORD_STATUS.COMPLETED]: { color: 'success', icon: <FiCheck />, label: 'Completed' },
      [RECORD_STATUS.IN_PROGRESS]: { color: 'primary', icon: <FiActivity />, label: 'In Progress' },
      [RECORD_STATUS.PENDING]: { color: 'warning', icon: <FiClock />, label: 'Pending' },
      [RECORD_STATUS.ON_HOLD]: { color: 'secondary', icon: <FiPause />, label: 'On Hold' },
      [RECORD_STATUS.CANCELLED]: { color: 'danger', icon: <FiX />, label: 'Cancelled' }
    };
    return configs[status] || { color: 'secondary', icon: <FiSquare />, label: status };
  }, []);

  const getPriorityConfig = useCallback((priority) => {
    const configs = {
      [RECORD_PRIORITY.CRITICAL]: { color: 'danger', label: 'Critical' },
      [RECORD_PRIORITY.HIGH]: { color: 'warning', label: 'High' },
      [RECORD_PRIORITY.MEDIUM]: { color: 'info', label: 'Medium' },
      [RECORD_PRIORITY.LOW]: { color: 'secondary', label: 'Low' }
    };
    return configs[priority] || { color: 'secondary', label: priority };
  }, []);

  const getCategoryConfig = useCallback((category) => {
    const configs = {
      [RECORD_CATEGORY.DIAGNOSIS]: { icon: <FiClipboard />, label: 'Diagnosis', color: 'primary' },
      [RECORD_CATEGORY.TREATMENT]: { icon: <FiActivity />, label: 'Treatment', color: 'success' },
      [RECORD_CATEGORY.PROCEDURE]: { icon: <FiSettings />, label: 'Procedure', color: 'info' },
      [RECORD_CATEGORY.MEDICATION]: { icon: <FiFilePlus />, label: 'Medication', color: 'warning' },
      [RECORD_CATEGORY.LAB_RESULT]: { icon: <FiBarChart />, label: 'Lab Result', color: 'secondary' },
      [RECORD_CATEGORY.IMAGING]: { icon: <FiEye />, label: 'Imaging', color: 'dark' },
      [RECORD_CATEGORY.CONSULTATION]: { icon: <FiUser />, label: 'Consultation', color: 'primary' },
      [RECORD_CATEGORY.FOLLOW_UP]: { icon: <FiCalendar />, label: 'Follow Up', color: 'info' },
      [RECORD_CATEGORY.OTHER]: { icon: <FiFileText />, label: 'Other', color: 'secondary' }
    };
    return configs[category] || { icon: <FiFileText />, label: category, color: 'secondary' };
  }, []);

  const getSeverityColor = useCallback((severity) => {
    const colors = {
      [RECORD_SEVERITY.SEVERE]: 'danger',
      [RECORD_SEVERITY.MODERATE]: 'warning',
      [RECORD_SEVERITY.MILD]: 'success'
    };
    return colors[severity] || 'secondary';
  }, []);

  const getDoctorNameById = useCallback((doctorId) => {
    if (!Array.isArray(doctors)) return doctorId || '-';
    const doctor = doctors.find(doc => doc._id === doctorId);
    return doctor ? `${doctor.name} ${doctor.surname}` : doctorId || '-';
  }, [doctors]);

  const showNotification = useCallback((message, type = 'info') => {
    if (window.Swal) {
      window.Swal.fire({
        icon: type === 'error' ? 'error' : type === 'success' ? 'success' : 'info',
        title: type === 'error' ? 'Error' : type === 'success' ? 'Success' : 'Information',
        text: message,
        timer: type === 'success' ? 3000 : 5000,
        toast: true,
        position: 'top-end',
        showConfirmButton: false
      });
    } else {
      console.log(`${type.toUpperCase()}: ${message}`);
    }
  }, []);

  const handleMostRecentAction = (action) => {
    setShowMostRecentDropdown(false);
    switch (action) {
      case 'sendMail':
        console.log('Send mail action');
        break;
      case 'sendMessage':
        console.log('Send message action');
        break;
      case 'downloadReport':
        console.log('Download report action');
        break;
      default:
        break;
    }
  };

  // Record actions
  const handleViewRecord = useCallback(async (recordId) => {
    try {
      setLoading(prev => ({ ...prev, recordDetails: true }));
      
      const existingRecord = Array.isArray(medicalRecords) ? 
        medicalRecords.find(record => record._id === recordId) : null;
        
      if (existingRecord) {
        setSelectedRecord(existingRecord);
        toggleOffcanvas('view', true);
        setLoading(prev => ({ ...prev, recordDetails: false }));
        return;
      }

      const response = await medicalRecordsService.getRecordById(recordId);
      setSelectedRecord(response.data || response);
      toggleOffcanvas('view', true);
    } catch (error) {
      console.error('Error loading record details:', error);
      showNotification(`Error loading record details: ${error.message}`, 'error');
    } finally {
      setLoading(prev => ({ ...prev, recordDetails: false }));
    }
  }, [medicalRecords, showNotification]);

  // Offcanvas and UI handlers
  const toggleOffcanvas = useCallback((offcanvasName, state = null) => {
    setOffcanvas(prev => ({
      ...prev,
      [offcanvasName]: state !== null ? state : !prev[offcanvasName]
    }));
  }, []);

  const toggleDropdown = useCallback((dropdownName, state = null) => {
    setDropdowns(prev => ({
      ...prev,
      [dropdownName]: state !== null ? state : !prev[dropdownName]
    }));
  }, []);

  // ================ API FUNCTIONS - CORREGIDAS ================
  
  const fetchDoctors = useCallback(async () => {
    setLoading(prev => ({ ...prev, doctors: true }));
    try {
      logDebug('Fetching doctors from', API_DOCTOR_LIST);
      
      const doctorsResponse = await fetch(`${API_DOCTOR_LIST}`);
      if (!doctorsResponse.ok) {
        throw new Error(`HTTP ${doctorsResponse.status}: Failed to fetch doctors`);
      }
      
      const doctorsData = await doctorsResponse.json();
      logDebug('Doctors response', doctorsData);
      
      const doctorsArray = Array.isArray(doctorsData?.data) ? doctorsData.data : 
                          Array.isArray(doctorsData) ? doctorsData : [];
      
      logDebug('Processed doctors array', doctorsArray);
      setDoctors(doctorsArray);
    } catch (err) {
      console.error('Error fetching doctors:', err);
      showNotification(`Error loading doctors: ${err.message}`, 'error');
      setDoctors([]);
    } finally {
      setLoading(prev => ({ ...prev, doctors: false }));
    }
  }, [showNotification, logDebug]);

  // ================ FUNCIÓN PRINCIPAL MEJORADA ================
  const loadMedicalRecords = useCallback(async () => {
    if (!patientId) {
      logDebug('No patientId provided, clearing records');
      setMedicalRecords([]);
      setFilteredRecords([]);
      return;
    }

    setLoading(prev => ({ ...prev, records: true }));
    
    try {
      // MEJORA 1: Query params más específicos
      const queryParams = {
        patientId: patientId.toString(),
        page: 1,
        limit: 1000, // Aumentar límite para obtener todos los registros
        sortBy: 'fromDate',
        sortOrder: 'desc'
      };

      logDebug('Loading medical records with params', queryParams);
      
      // MEJORA 2: Usar getAllRecords que es el método correcto según el controlador
      const response = await medicalRecordsService.getAllRecords(queryParams);
      
      logDebug('Raw API Response', response);

      // MEJORA 3: Estructura de respuesta mejorada basada en el controlador
      let recordsArray = [];
      
      if (response?.success && response?.data?.records) {
        // Estructura correcta del controlador: { success: true, data: { records: [...] } }
        recordsArray = Array.isArray(response.data.records) ? response.data.records : [];
        logDebug('Using response.data.records structure', recordsArray);
      } else if (response?.data && Array.isArray(response.data)) {
        // Estructura alternativa: { data: [...] }
        recordsArray = response.data;
        logDebug('Using response.data structure', recordsArray);
      } else if (Array.isArray(response)) {
        // Respuesta directa como array
        recordsArray = response;
        logDebug('Using direct array response', recordsArray);
      } else {
        logDebug('Unknown response structure, defaulting to empty array');
        recordsArray = [];
      }

      // MEJORA 4: Filtrar registros por patientId en el cliente como backup
      const filteredByPatient = recordsArray.filter(record => 
        record && record.patientId === patientId.toString()
      );

      logDebug('Filtered records by patientId', filteredByPatient);
      
      // MEJORA 5: Solo actualizar si hay cambios reales
      setMedicalRecords(prevRecords => {
        // Comparar por longitud y algunos IDs para detectar cambios
        if (prevRecords.length !== filteredByPatient.length) {
          logDebug('Records length changed, updating state');
          return filteredByPatient;
        }
        
        // Verificar si hay cambios en los primeros registros
        const hasChanges = filteredByPatient.slice(0, 5).some((record, index) => 
          !prevRecords[index] || record._id !== prevRecords[index]._id
        );
        
        if (hasChanges) {
          logDebug('Records content changed, updating state');
          return filteredByPatient;
        }
        
        logDebug('No changes detected, keeping previous state');
        return prevRecords;
      });
      
    } catch (error) {
      console.error('Error loading medical records:', error);
      showNotification(`Could not load medical records: ${error.message}`, 'error');
      setMedicalRecords([]);
    } finally {
      setLoading(prev => ({ ...prev, records: false }));
    }
  }, [patientId, showNotification, logDebug]);

  // MEJORA 6: Función de filtrado mejorada
  const applyFilters = useCallback(() => {
    logDebug('Applying filters to records', { 
      recordsCount: medicalRecords.length, 
      globalFilter, 
      filters 
    });
    
    if (!Array.isArray(medicalRecords)) {
      logDebug('Records is not an array, setting empty filtered records');
      setFilteredRecords([]);
      return;
    }

    let filtered = [...medicalRecords];
    logDebug('Starting with records', filtered.length);

    // Global search filter
    if (globalFilter && globalFilter.trim()) {
      const searchTerm = globalFilter.toLowerCase().trim();
      const beforeCount = filtered.length;
      
      filtered = filtered.filter(record => {
        if (!record) return false;
        
        const searchableFields = [
          record.title || '',
          record.description || '',
          record.notes || '',
          record.icdCode || '',
          getDoctorNameById(record.doctorId)
        ];
        
        return searchableFields.some(field => 
          field.toLowerCase().includes(searchTerm)
        );
      });
      
      logDebug(`Global filter applied: ${beforeCount} -> ${filtered.length}`);
    }

    // Apply specific filters
    Object.entries(filters).forEach(([key, value]) => {
      if (!value || value === 'All' || value === '') return;
      
      const beforeCount = filtered.length;
      
      switch (key) {
        case 'status':
        case 'priority':
        case 'category':
        case 'severity':
        case 'doctorId':
          filtered = filtered.filter(record => record && record[key] === value);
          break;
        case 'startDate':
          if (value) {
            filtered = filtered.filter(record => {
              if (!record || !record.fromDate) return false;
              try {
                const recordDate = new Date(record.fromDate);
                const filterDate = new Date(value);
                return recordDate >= filterDate;
              } catch (e) {
                logDebug('Error parsing start date', { recordDate: record.fromDate, filterDate: value });
                return false;
              }
            });
          }
          break;
        case 'endDate':
          if (value) {
            filtered = filtered.filter(record => {
              if (!record || !record.fromDate) return false;
              try {
                const recordDate = new Date(record.fromDate);
                const filterDate = new Date(value);
                return recordDate <= filterDate;
              } catch (e) {
                logDebug('Error parsing end date', { recordDate: record.fromDate, filterDate: value });
                return false;
              }
            });
          }
          break;
        default:
          break;
      }
      
      logDebug(`Filter ${key}=${value} applied: ${beforeCount} -> ${filtered.length}`);
    });

    logDebug('Final filtered records', filtered.length);
    setFilteredRecords(filtered);
  }, [medicalRecords, globalFilter, filters, getDoctorNameById, logDebug]);

  // MEJORA 7: Columnas con mejor manejo de errores
  const columns = useMemo(() => [
    {
      accessorKey: 'title',
      header: 'Diagnosed Conditions',
      size: 200,
      Cell: ({ cell, row }) => {
        try {
          const category = getCategoryConfig(row.original?.category || 'other');
          const title = cell.getValue() || 'No Title';
          return (
            <div>
              <div className="d-flex align-items-center gap-2">
                <span className={`badge bg-${category.color} d-flex align-items-center gap-1`}>
                  {category.icon}
                  <span style={{ fontSize: '0.7em' }}>{category.label}</span>
                </span>
              </div>
              <strong className="mt-1 d-block">{title}</strong>
              {row.original?.icdCode && (
                <small className="text-muted">ICD: {row.original.icdCode}</small>
              )}
            </div>
          );
        } catch (error) {
          console.error('Error rendering title cell:', error);
          return <div>Error loading</div>;
        }
      },
    },
    {
      accessorKey: 'fromDate',
      header: 'Timeline',
      size: 120,
      Cell: ({ cell, row }) => {
        try {
          const fromDate = formatDate(cell.getValue());
          const toDate = row.original?.toDate ? formatDate(row.original.toDate) : null;
          
          return (
            <div>
              <div>{fromDate}</div>
              {toDate && <small className="text-muted">to {toDate}</small>}
            </div>
          );
        } catch (error) {
          console.error('Error rendering timeline cell:', error);
          return <div>-</div>;
        }
      },
    },
    {
      accessorKey: 'toDate',
      header: 'To',
      size: 100,
      Cell: ({ cell }) => {
        try {
          const toDate = cell.getValue();
          return toDate ? formatDate(toDate) : <span className="text-muted">Ongoing</span>;
        } catch (error) {
          console.error('Error rendering toDate cell:', error);
          return <span className="text-muted">-</span>;
        }
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 120,
      Cell: ({ cell, row }) => {
        try {
          const status = cell.getValue() || 'pending';
          const priority = row.original?.priority || 'medium';
          const config = getStatusConfig(status);
          const priorityConfig = getPriorityConfig(priority);
          
          return (
            <div className="d-flex flex-column gap-1">
              <span className={`badge bg-${config.color} d-flex align-items-center gap-1`}>
                {config.icon}
                <span style={{ fontSize: '0.75em' }}>{config.label}</span>
              </span>
              {priority !== RECORD_PRIORITY.MEDIUM && (
                <span className={`badge bg-${priorityConfig.color}`} style={{ fontSize: '0.7em' }}>
                  {priorityConfig.label}
                </span>
              )}
            </div>
          );
        } catch (error) {
          console.error('Error rendering status cell:', error);
          return <div>-</div>;
        }
      },
    },
    {
      accessorKey: '_id',
      header: 'Action',
      size: 80,
      enableSorting: false,
      Cell: ({ cell, row }) => (
        <div className="d-flex gap-1">
          <button
            className="btn btn-link btn-sm text-primary p-1"
            onClick={(e) => {
              e.stopPropagation();
              handleViewRecord(cell.getValue());
            }}
            title="View"
          >
            <FiEye />
          </button>
        </div>
      ),
    },
  ], [formatDate, handleViewRecord, getCategoryConfig, getStatusConfig, getPriorityConfig]);

  // Resto de funciones (sin cambios mayores pero con mejor logging)
  const getActiveFilters = useCallback(() => {
    const activeFilters = [];
    
    if (globalFilter) {
      activeFilters.push({ key: 'search', label: `Search: "${globalFilter}"` });
    }
    
    const filterLabels = {
      status: 'Status',
      priority: 'Priority',
      category: 'Category',
      severity: 'Severity',
      doctorId: 'Doctor',
      startDate: 'From',
      endDate: 'To'
    };
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'All') {
        const label = filterLabels[key];
        let displayValue;
        
        if (['startDate', 'endDate'].includes(key)) {
          displayValue = new Date(value).toLocaleDateString();
        } else if (key === 'doctorId') {
          displayValue = getDoctorNameById(value);
        } else {
          displayValue = value;
        }
        
        activeFilters.push({ key, label: `${label}: ${displayValue}` });
      }
    });
    
    return activeFilters;
  }, [globalFilter, filters, getDoctorNameById]);

  const handleFormChange = useCallback((field, value, formType = 'new') => {
    setNewRecord(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleFilterChange = useCallback((filterKey, value) => {
    setFilters(prev => ({ ...prev, [filterKey]: value }));
  }, []);

  const removeFilter = useCallback((filterKey) => {
    if (filterKey === 'search') {
      setGlobalFilter('');
    } else {
      const resetValue = ['status', 'priority', 'category', 'severity'].includes(filterKey) ? 'All' : '';
      handleFilterChange(filterKey, resetValue);
    }
  }, [handleFilterChange]);

  const handleClearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS_STATE);
    setGlobalFilter('');
  }, []);

  const handleAddNew = useCallback(() => {
    setNewRecord({ ...DEFAULT_RECORD_STATE, patientId: patientId || '' });
    toggleOffcanvas('add', true);
  }, [patientId, toggleOffcanvas]);

  const handleSubmitNewRecord = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (loading.submitting) return;
    setLoading(prev => ({ ...prev, submitting: true }));
    
    try {
      const requiredFields = {
        title: 'Title',
        doctorId: 'Doctor',
        fromDate: 'Start Date',
        category: 'Category'
      };

      const missingFields = Object.keys(requiredFields).filter(field => 
        !newRecord[field] || newRecord[field].toString().trim() === ''
      );

      if (missingFields.length > 0) {
        const missingFieldNames = missingFields.map(field => requiredFields[field]);
        throw new Error(`Please fill in the following required fields: ${missingFieldNames.join(', ')}`);
      }

      const selectedDoctor = Array.isArray(doctors) ? 
        doctors.find(doc => doc._id === newRecord.doctorId) : null;
      const doctorName = selectedDoctor ? `${selectedDoctor.name} ${selectedDoctor.surname}` : '';

      const submissionData = {
        ...newRecord,
        doctorName,
        title: newRecord.title.trim(),
        description: (newRecord.description || '').trim(),
        notes: (newRecord.notes || '').trim(),
        icdCode: (newRecord.icdCode || '').trim(),
        createdBy: newRecord.doctorId
      };

      logDebug('Submitting new record', submissionData);

      const result = await medicalRecordsService.createRecord(submissionData);
      
      logDebug('Create record result', result);
      
      if (result.success !== false) {
        await loadMedicalRecords();
        toggleOffcanvas('add', false);
        showNotification(result.message || 'Medical record created successfully!', 'success');
      } else {
        throw new Error(result.message || 'Failed to create record');
      }
    } catch (error) {
      console.error('Error creating medical record:', error);
      showNotification(`Error creating record: ${error.message}`, 'error');
    } finally {
      setLoading(prev => ({ ...prev, submitting: false }));
    }
  }, [loading.submitting, newRecord, doctors, loadMedicalRecords, toggleOffcanvas, showNotification, logDebug]);

  // MEJORA 8: Effects con mejor logging y control
  useEffect(() => {
    logDebug('Component mounted, fetching doctors');
    fetchDoctors();
  }, [fetchDoctors]);

  useEffect(() => {
    logDebug('PatientId changed', { oldPatientId: 'previous', newPatientId: patientId });
    if (patientId) {
      loadMedicalRecords();
    } else {
      logDebug('No patientId, clearing records');
      setMedicalRecords([]);
      setFilteredRecords([]);
    }
  }, [patientId, loadMedicalRecords]);

  useEffect(() => {
    logDebug('Applying filters due to dependency change', {
      recordsLength: medicalRecords.length,
      filtersActive: Object.keys(filters).filter(k => filters[k] && filters[k] !== 'All').length,
      globalFilterActive: !!globalFilter
    });
    applyFilters();
  }, [applyFilters]);

  useEffect(() => {
    if (onCountUpdate && typeof onCountUpdate === 'function') {
      logDebug(`Updating counter for ${componentName}`, filteredRecords.length);
      onCountUpdate(componentName, filteredRecords.length);
    }
  }, [filteredRecords.length, onCountUpdate, componentName, logDebug]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown')) {
        setDropdowns(prev => ({ ...prev, mostRecent: false }));
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const activeFilters = getActiveFilters();

  // MEJORA 9: Mejor debugging en el render
  logDebug('Rendering component', {
    patientId,
    recordsCount: medicalRecords.length,
    filteredCount: filteredRecords.length,
    loading: loading.records,
    doctorsCount: doctors.length
  });

  return (
    <div className="card mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h6 className="mb-0">Medical Record</h6>
            
          </div>
    
          <div className="card-body">
            
    
            {/* Search and Actions Bar */}
            <div className="row mb-3">
              <div className="col-12">
                <div className="d-flex align-items-center gap-2">
                  <div className="input-group" style={{maxWidth: '300px'}}>
                    <span className="input-group-text">
                      <FiSearch />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search records..."
                      value={globalFilter}
                      onChange={(e) => setGlobalFilter(e.target.value)}
                    />
                  </div>
    
    {/* Most Recent Dropdown */}
                  <div className="dropdown" style={{position: 'relative'}}>
                    <button
                      className="btn btn-outline-secondary d-flex align-items-center gap-2"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowMostRecentDropdown(!showMostRecentDropdown);
                      }}
                    >
                      Most Recent
                      <FiChevronDown />
                    </button>
                    {showMostRecentDropdown && (
                      <div className="dropdown-menu show" style={{position: 'absolute', top: '100%', left: 0, zIndex: 1000}}>
                        <button
                          className="dropdown-item d-flex align-items-center gap-2"
                          onClick={() => handleMostRecentAction('sendMail')}
                        >
                          <FiMail />
                          Send Mail
                        </button>
                        <button
                          className="dropdown-item d-flex align-items-center gap-2"
                          onClick={() => handleMostRecentAction('sendMessage')}
                        >
                          <FiMessageSquare />
                          Send Message
                        </button>
                        <button
                          className="dropdown-item d-flex align-items-center gap-2"
                          onClick={() => handleMostRecentAction('downloadReport')}
                        >
                          <FiDownload />
                          Download report
                        </button>
                      </div>
                    )}
                  </div>
                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={() => toggleOffcanvas('filter')}
                  >
                    <FiFilter />
                    Filters
                    {activeFilters.length > 0 && (
                      <span className="badge bg-primary rounded-pill ms-1">
                        {activeFilters.length}
                      </span>
                    )}
                  </button>
    
                  
                </div>
              </div>
            </div>
    
            {/* Active Filters */}
            {activeFilters.length > 0 && (
              <div className="mb-3">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <small className="text-muted">Active filters:</small>
                  {activeFilters.map((filter) => (
                    <span key={filter.key} className="badge bg-light text-dark d-flex align-items-center gap-1">
                      {filter.label}
                      <button
                        className="btn btn-link p-0 ms-1"
                        style={{ fontSize: '0.8em', color: '#6c757d' }}
                        onClick={() => removeFilter(filter.key)}
                        aria-label={`Remove ${filter.label} filter`}
                      >
                        <FiX />
                      </button>
                    </span>
                  ))}
                  <button
                    className="btn btn-link btn-sm text-decoration-none"
                    onClick={handleClearFilters}
                  >
                    Clear all
                  </button>
                </div>
              </div>
            )}
    
            {/* Table */}
            <div className="table-responsive">
              {loading.records ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <div className="mt-2">Loading medical records...</div>
                </div>
              ) : (
                <MaterialReactTable
      columns={columns}
      data={filteredRecords}
      enableRowSelection
      onRowSelectionChange={setRowSelection}
      state={{
        rowSelection,
        globalFilter,
      }}
      onGlobalFilterChange={setGlobalFilter}
      enablePagination
      enableSorting
      enableColumnFilters={false}
      enableGlobalFilter={false}
      muiTableBodyRowProps={({ row }) => ({
        onClick: () => handleViewRecord(row.original._id),
        sx: { cursor: 'pointer' }
      })}
      initialState={{
        pagination: { pageSize: 10 },
        sorting: [{ id: 'fromDate', desc: true }]
      }}
    />
              )}
            </div>
    
            
          </div>
    
          {/* Add New Record Offcanvas */}
          <div
            className={`offcanvas offcanvas-end ${offcanvas.add ? 'show' : ''}`}
            tabIndex="-1"
            style={{ visibility: offcanvas.add ? 'visible' : 'hidden', width: '600px' }}
          >
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Add New Medical Record</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => toggleOffcanvas('add', false)}
              ></button>
            </div>
            <div className="offcanvas-body">
              <form onSubmit={handleSubmitNewRecord}>
                <div className="mb-3">
                  <label className="form-label">
                    Title <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={newRecord.title}
                    onChange={(e) => handleFormChange('title', e.target.value)}
                    maxLength={CONSTANTS.MAX_TITLE_LENGTH}
                    placeholder="Enter condition or procedure title"
                    required
                  />
                  <div className="form-text">
                    {newRecord.title.length}/{CONSTANTS.MAX_TITLE_LENGTH} characters
                  </div>
                </div>
    
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={newRecord.description}
                    onChange={(e) => handleFormChange('description', e.target.value)}
                    maxLength={CONSTANTS.MAX_DESCRIPTION_LENGTH}
                    placeholder="Detailed description of the condition or treatment"
                  />
                  <div className="form-text">
                    {newRecord.description.length}/{CONSTANTS.MAX_DESCRIPTION_LENGTH} characters
                  </div>
                </div>
    
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">
                      Category <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={newRecord.category}
                      onChange={(e) => handleFormChange('category', e.target.value)}
                      required
                    >
                      {CONSTANTS.CATEGORIES.map(category => {
                        const config = getCategoryConfig(category);
                        return (
                          <option key={category} value={category}>
                            {config.label}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Priority</label>
                    <select
                      className="form-select"
                      value={newRecord.priority}
                      onChange={(e) => handleFormChange('priority', e.target.value)}
                    >
                      {CONSTANTS.PRIORITIES.map(priority => {
                        const config = getPriorityConfig(priority);
                        return (
                          <option key={priority} value={priority}>
                            {config.label}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
    
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Severity</label>
                    <select
                      className="form-select"
                      value={newRecord.severity}
                      onChange={(e) => handleFormChange('severity', e.target.value)}
                    >
                      {CONSTANTS.SEVERITIES.map(severity => (
                        <option key={severity} value={severity}>
                          {severity.charAt(0).toUpperCase() + severity.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      value={newRecord.status}
                      onChange={(e) => handleFormChange('status', e.target.value)}
                    >
                      {CONSTANTS.STATUSES.map(status => {
                        const config = getStatusConfig(status);
                        return (
                          <option key={status} value={status}>
                            {config.label}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
    
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">
                      From Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={newRecord.fromDate}
                      onChange={(e) => handleFormChange('fromDate', e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">To Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={newRecord.toDate}
                      onChange={(e) => handleFormChange('toDate', e.target.value)}
                      min={newRecord.fromDate}
                    />
                  </div>
                </div>
    
                <div className="mb-3">
                  <label className="form-label">
                    Doctor <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={newRecord.doctorId}
                    onChange={(e) => handleFormChange('doctorId', e.target.value)}
                    required
                    disabled={loading.doctors}
                  >
                    <option value="">Select a doctor</option>
                    {Array.isArray(doctors) && doctors.map(doctor => (
                      <option key={doctor._id} value={doctor._id}>
                        {doctor.name} {doctor.surname} {doctor.specialization && `- ${doctor.specialization}`}
                      </option>
                    ))}
                  </select>
                  {loading.doctors && (
                    <div className="form-text">Loading doctors...</div>
                  )}
                </div>
    
                <div className="mb-3">
                  <label className="form-label">ICD Code</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newRecord.icdCode}
                    onChange={(e) => handleFormChange('icdCode', e.target.value)}
                    placeholder="Enter ICD-10 code (optional)"
                  />
                </div>
    
                <div className="mb-3">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    value={newRecord.notes}
                    onChange={(e) => handleFormChange('notes', e.target.value)}
                    maxLength={CONSTANTS.MAX_NOTES_LENGTH}
                    placeholder="Additional notes, observations, or treatment details"
                  />
                  <div className="form-text">
                    {newRecord.notes.length}/{CONSTANTS.MAX_NOTES_LENGTH} characters
                  </div>
                </div>
    
                <div className="d-flex gap-2 justify-content-end">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => toggleOffcanvas('add', false)}
                    disabled={loading.submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading.submitting}
                  >
                    {loading.submitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Creating...
                      </>
                    ) : (
                      <>
                        <FiPlus className="me-2" />
                        Create Record
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
    
          {/* View Record Offcanvas */}
          <div
            className={`offcanvas offcanvas-end ${offcanvas.view ? 'show' : ''}`}
            tabIndex="-1"
            style={{ visibility: offcanvas.view ? 'visible' : 'hidden', width: '550px' }}
          >
            <div className="offcanvas-header">
              <div>
                <h5 className="offcanvas-title mb-1">Medical Record Details</h5>
                <small className="opacity-75">ID: {selectedRecord?._id}</small>
              </div>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={() => toggleOffcanvas('view', false)}
              ></button>
            </div>
            <div className="offcanvas-body p-0" style={{backgroundColor: '#f8f9fa'}}>
              {loading.recordDetails ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <div className="mt-2">Loading record details...</div>
                </div>
              ) : selectedRecord ? (
                <>
                  {/* Record Header Card */}
                  <div className="card border-0 rounded-0 shadow-sm">
                    <div className="card-body">
                      <div className="d-flex align-items-start justify-content-between mb-3">
                        <div className="flex-grow-1">
                          <h4 className="card-title text-primary mb-1">{selectedRecord.title}</h4>
                          {selectedRecord.icdCode && (
                            <p className="card-subtitle text-muted mb-2">ICD Code: {selectedRecord.icdCode}</p>
                          )}
                          <div className="d-flex align-items-center gap-2 mb-2">
                            {(() => {
                              const categoryConfig = getCategoryConfig(selectedRecord.category);
                              return (
                                <span className={`badge bg-${categoryConfig.color} d-flex align-items-center gap-1`}>
                                  {categoryConfig.icon}
                                  {categoryConfig.label}
                                </span>
                              );
                            })()}
                            {selectedRecord.severity && (
                              <span className={`badge bg-${getSeverityColor(selectedRecord.severity)}`}>
                                {selectedRecord.severity.charAt(0).toUpperCase() + selectedRecord.severity.slice(1)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-end">
                          <div className="mb-2">
                            {(() => {
                              const statusConfig = getStatusConfig(selectedRecord.status);
                              return (
                                <span className={`badge bg-${statusConfig.color} d-flex align-items-center gap-1`}>
                                  {statusConfig.icon}
                                  {statusConfig.label}
                                </span>
                              );
                            })()}
                          </div>
                          {(() => {
                            const priorityConfig = getPriorityConfig(selectedRecord.priority);
                            return (
                              <span className={`badge bg-${priorityConfig.color}`}>
                                {priorityConfig.label} Priority
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                      
                      
                    </div>
                  </div>
    
                  {/* Record Information Card */}
                  <div className="card border-0 shadow-sm mt-3 mx-3">
                    <div className="card-header bg-white border-bottom">
                      <h6 className="card-title mb-0 d-flex align-items-center">
                        <FiClipboard className="me-2 text-primary" />
                        Record Information
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                            <span className="fw-medium text-muted">Record Title</span>
                            <span className="text-dark">{selectedRecord.title}</span>
                          </div>
                        </div>
                        
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                            <span className="fw-medium text-muted">Category</span>
                            {(() => {
                              const categoryConfig = getCategoryConfig(selectedRecord.category);
                              return (
                                <span className={`badge bg-${categoryConfig.color} d-flex align-items-center gap-1`}>
                                  {categoryConfig.icon}
                                  {categoryConfig.label}
                                </span>
                              );
                            })()}
                          </div>
                        </div>
                        
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                            <span className="fw-medium text-muted">Current Status</span>
                            {(() => {
                              const statusConfig = getStatusConfig(selectedRecord.status);
                              return (
                                <span className={`badge bg-${statusConfig.color} d-flex align-items-center gap-1`}>
                                  {statusConfig.icon}
                                  {statusConfig.label}
                                </span>
                              );
                            })()}
                          </div>
                        </div>
                        
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                            <span className="fw-medium text-muted">Priority Level</span>
                            {(() => {
                              const priorityConfig = getPriorityConfig(selectedRecord.priority);
                              return (
                                <span className={`badge bg-${priorityConfig.color}`}>
                                  {priorityConfig.label}
                                </span>
                              );
                            })()}
                          </div>
                        </div>
                        
                        {selectedRecord.severity && (
                          <div className="col-12">
                            <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                              <span className="fw-medium text-muted">Severity</span>
                              <span className={`badge bg-${getSeverityColor(selectedRecord.severity)}`}>
                                {selectedRecord.severity.charAt(0).toUpperCase() + selectedRecord.severity.slice(1)}
                              </span>
                            </div>
                          </div>
                        )}
                        
                        {selectedRecord.icdCode && (
                          <div className="col-12">
                            <div className="d-flex justify-content-between align-items-center py-2">
                              <span className="fw-medium text-muted">ICD Code</span>
                              <span className="text-dark font-monospace">{selectedRecord.icdCode}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
    
                  {/* Timeline Card */}
                  <div className="card border-0 shadow-sm mt-3 mx-3">
                    <div className="card-header bg-white border-bottom">
                      <h6 className="card-title mb-0 d-flex align-items-center">
                        <FiCalendar className="me-2 text-primary" />
                        Medical Timeline
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="timeline">
                        <div className="timeline-item d-flex align-items-center mb-3">
                          <div className="timeline-marker bg-primary rounded-circle me-3" style={{width: '12px', height: '12px'}}></div>
                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="fw-medium">Condition Started</span>
                              <span className="text-muted small">{formatDate(selectedRecord.fromDate)}</span>
                            </div>
                          </div>
                        </div>
                        
                        {selectedRecord.toDate ? (
                          <div className="timeline-item d-flex align-items-center mb-3">
                            <div className="timeline-marker bg-success rounded-circle me-3" style={{width: '12px', height: '12px'}}></div>
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-center">
                                <span className="fw-medium">Condition Ended</span>
                                <span className="text-muted small">{formatDate(selectedRecord.toDate)}</span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="timeline-item d-flex align-items-center mb-3">
                            <div className="timeline-marker bg-warning rounded-circle me-3" style={{width: '12px', height: '12px'}}></div>
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-center">
                                <span className="fw-medium">Ongoing Treatment</span>
                                <span className="badge bg-success">Active</span>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <div className="timeline-item d-flex align-items-center">
                          <div className="timeline-marker bg-info rounded-circle me-3" style={{width: '12px', height: '12px'}}></div>
                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="fw-medium">Record Created</span>
                              <span className="text-muted small">{formatDateTime(selectedRecord.createdAt)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
    
                  {/* Attending Doctor Card */}
                  <div className="card border-0 shadow-sm mt-3 mx-3">
                    <div className="card-header bg-white border-bottom">
                      <h6 className="card-title mb-0 d-flex align-items-center">
                        <FiUser className="me-2 text-primary" />
                        Attending Physician
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-3" style={{width: '50px', height: '50px'}}>
                          <FiUser className="text-white" size={20} />
                        </div>
                        <div className="flex-grow-1">
                          <div className="fw-medium h6 mb-1">
                            {getDoctorNameById(selectedRecord.doctorId)}
                          </div>
                          <small className="text-muted">
                            {(() => {
                              const doctor = Array.isArray(doctors) ? doctors.find(d => d._id === selectedRecord.doctorId) : null;
                              return doctor?.specialization || 'Medical Professional';
                            })()}
                          </small>
                          
                        </div>
                      </div>
                    </div>
                  </div>
    
                  {/* Description Card */}
                  {selectedRecord.description && (
                    <div className="card border-0 shadow-sm mt-3 mx-3">
                      <div className="card-header bg-white border-bottom">
                        <h6 className="card-title mb-0 d-flex align-items-center">
                          <FiFileText className="me-2 text-primary" />
                          Medical Description
                        </h6>
                      </div>
                      <div className="card-body">
                        <div className="bg-light rounded p-3">
                          <p className="mb-0 text-dark" style={{lineHeight: '1.6'}}>
                            {selectedRecord.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
    
                  {/* Clinical Notes Card */}
                  {selectedRecord.notes && (
                    <div className="card border-0 shadow-sm mt-3 mx-3">
                      <div className="card-header bg-white border-bottom">
                        <h6 className="card-title mb-0 d-flex align-items-center">
                          <FiClipboard className="me-2 text-primary" />
                          Clinical Notes
                        </h6>
                      </div>
                      <div className="card-body">
                        <div className="bg-light rounded p-3">
                          <p className="mb-0 text-dark" style={{lineHeight: '1.6', whiteSpace: 'pre-wrap'}}>
                            {selectedRecord.notes}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
    
                  {/* Record Statistics Card */}
                  <div className="card border-0 shadow-sm mt-3 mx-3">
                    <div className="card-header bg-white border-bottom">
                      <h6 className="card-title mb-0 d-flex align-items-center">
                        <FiBarChart className="me-2 text-primary" />
                        Record Statistics
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row text-center">
                        <div className="col-4">
                          <div className="border-end">
                            <div className="h5 mb-1 text-primary">
                              {(() => {
                                if (!selectedRecord.fromDate) return 0;
                                const startDate = new Date(selectedRecord.fromDate);
                                const endDate = selectedRecord.toDate ? new Date(selectedRecord.toDate) : new Date();
                                const diffTime = Math.abs(endDate - startDate);
                                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                return diffDays;
                              })()}
                            </div>
                            <small className="text-muted">Duration (Days)</small>
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="border-end">
                            <div className="h5 mb-1 text-success">
                              {(() => {
                                const created = new Date(selectedRecord.createdAt);
                                const now = new Date();
                                const diffTime = Math.abs(now - created);
                                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                return diffDays;
                              })()}
                            </div>
                            <small className="text-muted">Days Recorded</small>
                          </div>
                        </div>
                        <div className="col-4">
                          <div className="h5 mb-1 text-info">
                            {selectedRecord.status === RECORD_STATUS.COMPLETED ? '✓' : 
                             selectedRecord.status === RECORD_STATUS.IN_PROGRESS ? '⚡' : 
                             selectedRecord.status === RECORD_STATUS.PENDING ? '⏳' : '⚠️'}
                          </div>
                          <small className="text-muted">Status</small>
                        </div>
                      </div>
                    </div>
                  </div>
    
                  {/* Record Metadata Card */}
                  <div className="card border-0 shadow-sm mt-3 mx-3 mb-3">
                    <div className="card-header bg-white border-bottom">
                      <h6 className="card-title mb-0 d-flex align-items-center">
                        <FiActivity className="me-2 text-primary" />
                        Record Metadata
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                            <span className="fw-medium text-muted">Record Created</span>
                            <span className="text-dark">{formatDateTime(selectedRecord.createdAt)}</span>
                          </div>
                        </div>
                        
                        {selectedRecord.updatedAt && selectedRecord.updatedAt !== selectedRecord.createdAt && (
                          <div className="col-12">
                            <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                              <span className="fw-medium text-muted">Last Updated</span>
                              <span className="text-dark">{formatDateTime(selectedRecord.updatedAt)}</span>
                            </div>
                          </div>
                        )}
                        
                        {selectedRecord.createdBy && (
                          <div className="col-12">
                            <div className="d-flex justify-content-between align-items-center py-2">
                              <span className="fw-medium text-muted">Created By</span>
                              <span className="text-dark">{getDoctorNameById(selectedRecord.createdBy)}</span>
                            </div>
                          </div>
                        )}
                      </div>
    
                      
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-5">
                  <FiAlertTriangle size={48} className="text-muted mb-3" />
                  <h6 className="text-muted">Record not found</h6>
                  <p className="text-muted">The selected record could not be loaded.</p>
                  <button
                    className="btn btn-primary"
                    onClick={() => toggleOffcanvas('view', false)}
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
    
          {/* Filter Offcanvas */}
          <div
            className={`offcanvas offcanvas-end ${offcanvas.filter ? 'show' : ''}`}
            tabIndex="-1"
            style={{ visibility: offcanvas.filter ? 'visible' : 'hidden', width: '400px' }}
          >
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Filter Records</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => toggleOffcanvas('filter', false)}
              ></button>
            </div>
            <div className="offcanvas-body">
              <div className="mb-3">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="All">All Statuses</option>
                  {CONSTANTS.STATUSES.map(status => {
                    const config = getStatusConfig(status);
                    return (
                      <option key={status} value={status}>
                        {config.label}
                      </option>
                    );
                  })}
                </select>
              </div>
    
              <div className="mb-3">
                <label className="form-label">Priority</label>
                <select
                  className="form-select"
                  value={filters.priority}
                  onChange={(e) => handleFilterChange('priority', e.target.value)}
                >
                  <option value="All">All Priorities</option>
                  {CONSTANTS.PRIORITIES.map(priority => {
                    const config = getPriorityConfig(priority);
                    return (
                      <option key={priority} value={priority}>
                        {config.label}
                      </option>
                    );
                  })}
                </select>
              </div>
    
              <div className="mb-3">
                <label className="form-label">Category</label>
                <select
                  className="form-select"
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                >
                  <option value="All">All Categories</option>
                  {CONSTANTS.CATEGORIES.map(category => {
                    const config = getCategoryConfig(category);
                    return (
                      <option key={category} value={category}>
                        {config.label}
                      </option>
                    );
                  })}
                </select>
              </div>
    
              <div className="mb-3">
                <label className="form-label">Severity</label>
                <select
                  className="form-select"
                  value={filters.severity}
                  onChange={(e) => handleFilterChange('severity', e.target.value)}
                >
                  <option value="All">All Severities</option>
                  {CONSTANTS.SEVERITIES.map(severity => (
                    <option key={severity} value={severity}>
                      {severity.charAt(0).toUpperCase() + severity.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
    
              <div className="mb-3">
                <label className="form-label">Doctor</label>
                <select
                  className="form-select"
                  value={filters.doctorId}
                  onChange={(e) => handleFilterChange('doctorId', e.target.value)}
                  disabled={loading.doctors}
                >
                  <option value="">All Doctors</option>
                  {Array.isArray(doctors) && doctors.map(doctor => (
                    <option key={doctor._id} value={doctor._id}>
                      {doctor.name} {doctor.surname}
                    </option>
                  ))}
                </select>
              </div>
    
              <div className="row mb-3">
                <div className="col-6">
                  <label className="form-label">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  />
                </div>
                <div className="col-6">
                  <label className="form-label">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    min={filters.startDate}
                  />
                </div>
              </div>
    
              <div className="d-flex gap-2 justify-content-end">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleClearFilters}
                >
                  Clear All
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => toggleOffcanvas('filter', false)}
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
    
          {/* Backdrop */}
          {(offcanvas.add || offcanvas.view || offcanvas.filter) && (
            <div
              className="offcanvas-backdrop fade show"
              onClick={() => {
                setOffcanvas({ add: false, view: false, filter: false });
              }}
            ></div>
          )}
        </div>
      );
    };
    
    export default MedicalRecordTreatments;